"use client";

import React, { createContext, useContext, ReactNode, useReducer } from "react";
import { Widget } from "../types";

interface WidgetState {
  widgets: Widget[];
  widgetsByPage: Record<number, Widget[]>; // * here we store the widgets that came from the dashboard load - later we use it for switching pages.
  loading: boolean;
  error: string | null;
}

type WidgetAction =
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_ERROR"; payload: string | null }
  | { type: "SET_CURRENT_WIDGETS"; payload: Widget[] }
  | { type: "SET_WIDGETS_BY_PAGE"; payload: Record<number, Widget[]> }
  | { type: "ADD_WIDGET"; payload: Widget }
  | { type: "UPDATE_WIDGET"; payload: Widget }
  | { type: "DELETE_WIDGET"; payload: number }

const initialState: WidgetState = {
  widgets: [],
  widgetsByPage: {},
  loading: false,
  error: null,
};


const widgetReducer = (state: WidgetState, action: WidgetAction): WidgetState => {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    case "SET_ERROR":
      return { ...state, error: action.payload, loading: false };
    case "SET_CURRENT_WIDGETS":
      return { ...state, widgets: action.payload, loading: false };
    case "ADD_WIDGET":
      const normalizedWidget = { ...action.payload, id: Number(action.payload.id) };
      return { ...state, widgets: [...state.widgets, normalizedWidget] };
    case "UPDATE_WIDGET":
      const updateId = Number(action.payload.id);
      const updatedPayload = { ...action.payload, updatedAt: new Date().toISOString() };
      return { ...state, widgets: state.widgets.map(widget => Number(widget.id) === updateId ? updatedPayload : widget) };
    case "DELETE_WIDGET":
      return { ...state, widgets: state.widgets.filter(widget => Number(widget.id) !== Number(action.payload)) };
    default:
      return state;
  }
};

const WidgetContext = createContext<{
  state: WidgetState;
  dispatch: React.Dispatch<WidgetAction>;
} | null>(null);
WidgetContext.displayName = "WidgetContext";

interface WidgetProviderProps {
  children: ReactNode;
}

export const WidgetProvider: React.FC<WidgetProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(widgetReducer, initialState);


  return (
    <WidgetContext.Provider value={{ state, dispatch }}>
      {children}
    </WidgetContext.Provider>
  );
};

export const useWidget = () => {
  const context = useContext(WidgetContext);
  if (!context) {
    throw new Error("useWidget must be used within a WidgetProvider");
  }
  return context;
};
